/**
 * When window.cartAlwaysNewLineItem is true, increasing cart quantity
 * duplicates the line via /cart/add instead of /cart/change.
 */
(function () {
 try {
  function generateFallbackUid() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (error) {
      /* noop */
    }
    return `uid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function getLineItemUid() {
    try {
      if (typeof window.getCartLineItemUid === 'function') {
        const uid = window.getCartLineItemUid();
        if (uid) return uid;
      }
    } catch (error) {
      console.warn('[cart-line-duplicator] getCartLineItemUid failed', error);
    }
    return generateFallbackUid();
  }

  function getCartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function getCartItemsElement(input) {
    return (
      input?.closest('cart-items') ||
      input?.closest('cart-drawer-items') ||
      document.querySelector('cart-drawer-items') ||
      document.querySelector('cart-items')
    );
  }

  // Relationship/allocation properties that tie a line to a specific gift-bag or
  // matching-upsell line. A duplicate must NOT inherit these, otherwise it links to
  // the source line's upsell/gift bag and won't show its own upsell block.
  const EXCLUDED_DUPLICATE_PROPERTIES = [
    '_matchingPillowId',
    '_matchingPillowIdLinkedToProduct',
    '_mpFreeProductQuantity',
    '_mpPaidProductQuantity',
    '_giftBagId',
    '_giftBagIdLinkedToProduct',
    '_gbFreeProductQuantity',
    '_gbPaidProductQuantity',
  ];

  function buildDuplicateLinePayload(item) {
    if (!item || item.variant_id == null) {
      throw new Error('Cannot build duplicate payload: missing item or variant_id');
    }

    const params = new URLSearchParams();
    params.append('id', String(item.variant_id));
    params.append('quantity', '1');

    Object.keys(item.properties || {}).forEach((key) => {
      if (EXCLUDED_DUPLICATE_PROPERTIES.indexOf(key) !== -1) return;
      const value = item.properties[key];
      if (value != null && String(value) !== '') {
        params.append(`properties[${key}]`, String(value));
      }
    });
    params.set('properties[_line_item_uid]', getLineItemUid());

    if (item.selling_plan_allocation?.selling_plan?.id) {
      params.append('selling_plan', String(item.selling_plan_allocation.selling_plan.id));
    }

    return params.toString();
  }

  async function addDuplicateLineToCart(payload, cartItemsElement) {
    if (window.CartManager?.addProductToCart) {
      return window.CartManager.addProductToCart(payload);
    }

    const params = new URLSearchParams(payload);
    const sectionsElement =
      cartItemsElement || document.querySelector('cart-items') || document.querySelector('cart-drawer-items');

    let sections = [{ section: 'cart-icon-bubble' }];
    if (sectionsElement?.getSectionsToRender) {
      sections = sectionsElement
        .getSectionsToRender()
        .filter((section) => section.section && section.section !== 'undefined');
    }

    sections.forEach((section) => {
      params.append('sections[]', section.section);
    });
    params.append('sections_url', window.location.pathname);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error(`Add to cart failed: ${response.statusText}`);
    }

    return response.json();
  }

  async function updateCartAfterDuplicate(cartResponse, input) {
    // Treat this like the upsell "add" flow: /cart/add.js returns the added line item
    // (no full `items` array), so pass quantityElement = null and line = 0 to avoid
    // the quantity-diff branch in updateCartDOM that reads parsedState.items.length.
    const items = document.querySelectorAll('.cart-item');

    if (window.CartManager) {
      if (window.CartManager.hasSectionsData(cartResponse)) {
        await window.CartManager.updateCartDOM(cartResponse, 0, items, null);
        return;
      }

      await window.CartManager.reloadCartSection();
      return;
    }

    const cartItemsElement = getCartItemsElement(input);

    if (cartItemsElement?.updateCartDOM && cartResponse?.sections) {
      cartItemsElement.updateCartDOM(cartResponse, 0, items, null);
      return;
    }

    if (cartItemsElement?.onCartUpdate) {
      await cartItemsElement.onCartUpdate();
    }
  }

  async function fetchCartItem({ lineIndex, itemKey }) {
    const response = await fetch(`${getCartUrl()}.js`, {
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Fetch cart failed: ${response.statusText}`);
    }

    const cart = await response.json();
    const items = (cart && cart.items) || [];

    if (itemKey) {
      return items.find((item) => item.key === itemKey) || null;
    }

    if (lineIndex) {
      return items[lineIndex - 1] || null;
    }

    return null;
  }

  async function duplicateCartLineItem(item, input) {
    if (!item || !window.cartAlwaysNewLineItem) return false;

    const cartItemsElement = getCartItemsElement(input);
    const lineIndex = parseInt(input?.dataset?.index, 10) || 0;

    if (cartItemsElement?.enableLoading && lineIndex) {
      cartItemsElement.enableLoading(lineIndex);
    }

    try {
      const payload = buildDuplicateLinePayload(item);
      const cartResponse = await addDuplicateLineToCart(payload, cartItemsElement);

      if (cartResponse.status) {
        throw new Error(cartResponse.description || cartResponse.message || 'Add to cart failed');
      }

      await updateCartAfterDuplicate(cartResponse, input);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartDataUpdated, {
          source: 'cart-line-duplicator',
          lineItems: cartResponse.items || [],
        }).catch(() => {});
      }

      return true;
    } catch (error) {
      console.log('[cart-line-duplicator]', error);
      if (input) {
        input.value = input.getAttribute('value') || input.value;
      }
      return false;
    } finally {
      if (cartItemsElement?.disableLoading && lineIndex) {
        cartItemsElement.disableLoading(lineIndex);
      }
    }
  }

  window.duplicateCartLineFromInput = async function (input) {
    if (!window.cartAlwaysNewLineItem || !input || input.name !== 'updates[]') {
      return false;
    }

    try {
      const lineIndex = parseInt(input.dataset.index, 10);
      if (!lineIndex) return false;

      const item = await fetchCartItem({ lineIndex });
      return await duplicateCartLineItem(item, input);
    } catch (error) {
      console.log('[cart-line-duplicator]', error);
      return false;
    }
  };

  window.duplicateCartLineFromKey = async function (itemKey, input) {
    if (!window.cartAlwaysNewLineItem || !itemKey) {
      return false;
    }

    try {
      const item = await fetchCartItem({ itemKey });
      return await duplicateCartLineItem(item, input);
    } catch (error) {
      console.log('[cart-line-duplicator]', error);
      return false;
    }
  };

  window.CartLineDuplicator = {
    EXCLUDED_DUPLICATE_PROPERTIES,
    buildLinePayload: buildDuplicateLinePayload,
    addLineToCart: addDuplicateLineToCart,
    rerenderCart: updateCartAfterDuplicate,
  };
 } catch (error) {
  console.log('[cart-line-duplicator] init failed', error);
 }
})();
